/* * File: /src/store/modules/realtime.js * Created Date: Saturday November 27th 2021
 * Author: Nicolas Wegener, Roy Collins
 * -----
 * Last Modified: Sunday, 28th November 2021 11:51:45 am
 * Modified By: Nicolas Wegener (wegenern@gmail.com)
 * -----
 * Copyright (c) 2021 AdBase, Inc.
 */

import voiceService from '../../services/voiceServices';
import agentService from '../../services/agentService';

// Stores all the realtime status we need to track.
// Perhaps break this out later as we get more
const state = {
  calls: [],
  segments: [],
  agents: [],
  dispositions: [], // conversation?
  activeDisposition: undefined, // Current viewed disposition,
  activeDispositionAt: undefined, // last change time for watchers
  callData: [], // incoming data per call_id, might be late
  // These are used to UI indicators
  haveNewAirbnb: false, // assigned or unread per config
  newAirbnbThreadId: 0, // If set then newest Id is from this contact
  haveNewSms: false, // assigned or unread per config
  newSmsContactId: 0, // If set then newest Id is from this contact
  haveNewMessage: false,
  newMessageContactId: 0, // If set then newest Id is from this contact
  haveNewWhatsapp: 0, // assigned or unread
  newWhatsappContactId: 0, // If set then newest Id is from this contact
  haveNewVoicemail: false,
  haveIncompleteVoicemail: false,
  haveNewLead: false, // assigned lead
  haveNewTask: false, // assigned task
  latestConversation: undefined,
  latestContactTimeline: undefined,
  latestConversationEvent: undefined, // typing etc from other agents
  unreadNotifications: 0, // user_notifications inbox badge
  showNotificationDrawer: false,
};

const getters = {
  getActiveCalls: (state) => {
    return state.calls;
  },
  getActiveDisposition: (state) => {
    return state.activeDisposition;
  },
  getActiveDispositionAt: (state) => {
    return state.activeDispositionAt;
  },
  getActiveSegments: (state) => {
    return state.segments;
  },
  getActiveAgents: (state) => {
    return state.agents;
  },
  getCallById: (state) => (callId) => {
    return state.calls.find((c) => c.call_id == callId);
  },
  getMySegments: (state) => (userId) => {
    return state.segments.filter((s) => s.user_id == userId);
  },
  getMyAnsweredSegments: (state) => (userId) => {
    return state.segments.filter(
      (s) => s.user_id == userId && s.segment_status == 'answered'
    );
  },
  getMyUnansweredSegments: (state) => (userId) => {
    return state.segments.filter(
      (s) => s.user_id == userId && s.segment_status != 'answered'
    );
  },
  getCallSegments: (state) => (callId) => {
    return state.segments.filter((s) => s.call_id == callId);
  },
  getMyUnansweredBandwidthCalls: (state) => (userId) => {
    const myCalls = state.calls.filter(
      (c) => c.user_id == userId && c.direction == 'outbound'
    );
    if (myCalls.length == 0) {
      return false;
    }
    // Return true on first dialing segment, if any
    return myCalls.some((c) => {
      return (
        state.segments.filter(
          (s) =>
            s.call_id == c.call_id &&
            s.segment_type == 'contact' &&
            s.segment_status == 'ringing'
        ).length > 0
      );
    });
  },
  getMyDispositions: (state) => (userId) => {
    return state.dispositions.filter((d) => d.user_id == userId);
  },
  getDispositionById: (state) => (dispId) => {
    return state.dispositions.find((d) => d.id == dispId);
  },
  getMyAgentStatus: (state) => (userId) => {
    return state.agents.find((a) => a.user_id === userId);
  },
  getCallData: (state) => (callId) => {
    const myCd = state.callData.filter((cd) => cd.call_id === callId);
    return myCd ? myCd[0] : undefined;
  },
  getHaveNewAirbnb: (state) => state.haveNewAirbnb,
  getNewAirbnbThreadId: (state) => state.newAirbnbThreadId,
  getHaveNewSms: (state) => state.haveNewSms,
  getNewSmsContactId: (state) => state.newSmsContactId,
  getHaveNewWhatsapp: (state) => state.haveNewWhatsapp,
  getNewWhatsappContactId: (state) => state.newSmsContactId,
  getHaveNewMessage: (state) => state.haveNewMessage,
  getNewMessageContactId: (state) => state.newMessageContactId,
  getHaveNewVoicemail: (state) => state.haveNewVoicemail,
  getHaveIncompleteVoicemail: (state) => state.haveIncompleteVoicemail,
  getHaveNewLead: (state) => state.haveNewLead,
  getHaveNewTask: (state) => state.haveNewTask,
  getLatestConversation: (state) => state.latestConversation,
  getLatestContactTimeline: (state) => state.latestContactTimeline,
  getLatestConversationEvent: (state) => state.latestConversationEvent,
  getUnreadNotifications: (state) => state.unreadNotifications,
  getShowNotificationDrawer: (state) => state.showNotificationDrawer,
};
const actions = {
  fetchActiveCalls({ commit }) {
    return new Promise((resolve, reject) => {
      voiceService
        .getActiveCalls()
        .then((r) => {
          commit('setCalls', r.data);
          resolve(r.data);
        })
        .catch((e) => {
          console.log('Error loading active calls', e);
          reject(e);
        });
    });
  },
  fetchActiveSegments({ commit }) {
    return new Promise((resolve, reject) => {
      voiceService
        .getActiveSegments()
        .then((r) => {
          commit('setSegments', r.data);
          resolve(r.data);
        })
        .catch((e) => {
          console.log('Error loading active segments', e);
          reject(e);
        });
    });
  },
  fetchActiveAgents({ commit }, userId) {
    return new Promise((resolve, reject) => {
      agentService
        .getActiveAgents()
        .then((r) => {
          // Only set agents that are not this user as it will be a race condition
          // since this is called on login and this might complete after the websocket
          // login updates the initial agent state of the logging in user
          const temp = r
            .filter((x) => x.user_id != userId)
            .map((y) => {
              // Not fancy descructuring but explicit
              return {
                modified_at: y.modified_at,
                state: y.state,
                status: y.status,
                type: 'agentStatus',
                user_id: y.user_id,
              };
            });
          /*
          if (!temp.find(x => x.user_id == userId)) {
            temp.push({
              modified_at: new Date(),
              state: 'away',
              status: 'pending',
              type: 'agentStatus',
              user_id: userId,
            });
          }
          */
          commit('setAgents', temp);
          resolve(temp);
        })
        .catch((e) => {
          console.log('Error loading active agents', e);
          reject(e);
        });
    });
  },
  modifyCall({ commit }, call) {
    if (call.status == 'completed') {
      commit('removeCall', call);
    } else {
      commit('mutateCall', call);
    }
  },
  modifySegment({ commit }, seg) {
    const finalStatuses = [
      'completed',
      'failed',
      'no-answer',
      'busy',
      'canceled',
    ];
    if (finalStatuses.includes(seg.segment_status)) {
      commit('removeSegment', seg);
    } else {
      commit('mutateSegment', seg);
    }
  },
  modifyAgent({ commit }, agent) {
    if (agent.status == 'logout') {
      commit('removeAgent', agent);
    } else {
      commit('mutateAgent', agent);
    }
  },
  modifyDisposition({ commit }, disp) {
    if (disp.status == 'completed') {
      // Call data is needed until disp is gone
      commit('removeCallData', disp.call_id);
      commit('removeDisposition', disp);
    } else {
      commit('mutateDisposition', disp);
    }
  },
  modifyActiveDisposition({ commit }, dispId) {
    commit('setActiveDisposition', dispId);
  },
  addCallData({ commit }, cd) {
    commit('addCallData', cd);
  },
  modifyHaveNewAirbnb({ commit }, haveNew) {
    commit('setHaveNewAirbnb', haveNew);
  },
  modifyHaveNewSms({ commit }, haveNew) {
    commit('setHaveNewSms', haveNew);
  },
  modifyHaveNewWhatsapp({ commit }, haveNew) {
    commit('setHaveNewWhatsapp', haveNew);
  },
  modifyNewAirbnbThreadId({ commit }, newId) {
    commit('setNewAirbnbThreadId', newId);
  },
  modifyNewSmsContactId({ commit }, newId) {
    commit('setNewSmsContactId', newId);
  },
  modifyNewWhatsappContactId({ commit }, newId) {
    commit('setNewWhatsappContactId', newId);
  },
  modifyHaveNewMessage({ commit }, haveNew) {
    commit('setHaveNewMessage', haveNew);
  },
  modifyNewMessageContactId({ commit }, newId) {
    commit('setNewMessageContactId', newId);
  },
  modifyHaveNewVoicemail({ commit }, haveNew) {
    commit('setHaveNewVoicemail', haveNew);
  },
  modifyHaveIncompleteVoicemail({ commit }, haveNew) {
    commit('setHaveIncompleteVoicemail', haveNew);
  },
  modifyHaveNewLead({ commit }, haveNew) {
    commit('setHaveNewLead', haveNew);
  },
  modifyHaveNewTask({ commit }, haveNew) {
    commit('setHaveNewTask', haveNew);
  },
  modifyLatestConversation({ commit }, newConv) {
    commit('setLatestConversation', newConv);
  },
  modifyLatestContactTimeline({ commit }, newT) {
    commit('setLatestContactTimeline', newT);
  },
  modifyUnreadNotifications({ commit }, count) {
    commit('setUnreadNotifications', count);
  },
  // +1 on a live alert, -1 when one is read
  adjustUnreadNotifications({ commit, state }, delta) {
    commit('setUnreadNotifications', state.unreadNotifications + delta);
  },
  modifyShowNotificationDrawer({ commit }, show) {
    commit('setShowNotificationDrawer', show);
  },
  modifyLatestConversationEvent({ commit }, event) {
    commit('setLatestConversationEvent', event);
  },
};
const mutations = {
  setCalls(state, calls) {
    state.calls = Object.assign([], calls);
    for (const c of state.calls) {
      if (c.queue_id) {
        c.last_queue_id = c.queue_id;
      }
    }
  },
  setAgents(state, agents) {
    state.agents = Object.assign([], agents);
  },
  setSegments(state, segments) {
    state.segments = Object.assign([], segments);
  },
  setActiveDisposition(state, dispId) {
    state.activeDisposition = dispId;
    state.activeDispositionAt = new Date();
  },
  mutateCall(state, call) {
    // The patch with null is a little cranky so we remove with 0
    for (const a in call) {
      if (call[a] === 0) {
        call[a] = null;
      }
    }
    // queue_id is cleared on answer so keep the last queue the call was in
    if (call.queue_id) {
      call.last_queue_id = call.queue_id;
    }
    const idx = state.calls.findIndex((x) => x.call_id == call.call_id);
    if (idx === -1) {
      // Prevent out of order events from adding removed calls back.
      // Calls where an agent is removed after a call ends will not add it back
      if (call.status != undefined) {
        state.calls.push(call);
      }
    } else {
      state.calls[idx] = Object.assign(state.calls[idx], call);
    }
  },
  removeCall(state, call) {
    state.calls = state.calls.filter((c) => c.call_id != call.call_id);
  },
  mutateSegment(state, segment) {
    // The patch with null is a little cranky so we remove with 0
    for (const a in segment) {
      if (segment[a] === 0) {
        segment[a] = null;
      }
    }
    const idx = state.segments.findIndex(
      (s) => s.segment_id == segment.segment_id
    );
    if (idx === -1) {
      state.segments.push(segment);
    } else {
      state.segments[idx] = Object.assign(state.segments[idx], segment);
    }
  },
  removeSegment(state, seg) {
    state.segments = state.segments.filter(
      (s) => s.segment_id != seg.segment_id
    );
  },
  mutateAgent(state, agent) {
    const idx = state.agents.findIndex((x) => x.user_id == agent.user_id);
    if (idx === -1) {
      state.agents.push(agent);
    } else {
      state.agents[idx] = Object.assign(state.agents[idx], agent);
    }
  },
  removeAgent(state, agent) {
    state.agents = state.agents.filter((a) => a.user_id != agent.user_id);
  },
  mutateDisposition(state, disp) {
    // The patch with null is a little cranky so we remove with 0
    for (const a in disp) {
      if (disp[a] === 0) {
        disp[a] = undefined;
      }
    }
    const idx = state.dispositions.findIndex((x) => x.id == disp.id);
    if (idx === -1) {
      state.dispositions.push(disp);
    } else {
      state.dispositions[idx] = Object.assign(state.dispositions[idx], disp);
    }
  },
  removeDisposition(state, disp) {
    if (state.activeDisposition == disp.id) {
      state.activeDisposition = undefined;
    }
    state.dispositions = state.dispositions.filter((d) => d.id != disp.id);
  },
  addCallData(state, cd) {
    state.callData.push(cd);
  },
  removeCallData(state, call_id) {
    state.callData = state.callData.filter((c) => c.call_id != call_id);
  },
  setHaveNewAirbnb(state, haveNew) {
    state.haveNewAirbnb = haveNew;
  },
  setHaveNewSms(state, haveNew) {
    state.haveNewSms = haveNew;
  },
  setHaveNewWhatsapp(state, haveNew) {
    state.haveNewWhatsapp = haveNew;
  },
  setNewAirbnbThreadId(state, newId) {
    state.newAirbnbThreadId = newId;
  },
  setNewSmsContactId(state, newId) {
    state.newSmsContactId = newId;
  },
  setNewWhatsappContactId(state, newId) {
    state.newSmsContactId = newId;
  },
  setHaveNewMessage(state, haveNew) {
    state.haveNewMessage = haveNew;
  },
  setNewMessageContactId(state, newId) {
    state.newMessageContactId = newId;
  },
  setHaveNewVoicemail(state, haveNew) {
    state.haveNewVoicemail = haveNew;
  },
  setHaveIncompleteVoicemail(state, haveNew) {
    state.haveIncompleteVoicemail = haveNew;
  },
  setHaveNewLead(state, haveNew) {
    state.haveNewLead = haveNew;
  },
  setHaveNewTask(state, haveNew) {
    state.haveNewTask = haveNew;
  },
  setLatestConversation(state, latest) {
    state.latestConversation = latest;
  },
  setLatestContactTimeline(state, latest) {
    state.latestContactTimeline = latest;
  },
  setUnreadNotifications(state, count) {
    state.unreadNotifications = Math.max(0, count || 0);
  },
  setShowNotificationDrawer(state, show) {
    state.showNotificationDrawer = !!show;
  },
  setLatestConversationEvent(state, event) {
    state.latestConversationEvent = event;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
